import { memo, useCallback, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Badge, Button, CartContainer } from '@aimsos/ui';

import { formatDateWithYear } from '../../../utils/traitDates.js';
import SandboxClaimRow from './SandboxClaimRow.jsx';

import { ArrowUpRight, ChevronDown, ChevronUp, FileText, History, Mail, NotebookPen, Phone, Sparkles } from 'lucide-react';

/** The icon of each kind of source, as in the prototype. */
const CHANNEL_ICONS = Object.freeze({
  call: Phone,
  email: Mail,
  notes: NotebookPen,
  history: History,
  inferred: Sparkles,
});

/**
 * One source of a Sandbox (CT-5718): the icon of its kind (call, email, notes,
 * history, inferred), "Call · Renewal follow-up", its date (with the year) and
 * claim count, a Conflict chip while one of its claims disagrees, the source
 * type, Open ↗ (where it came from) and Hide / "Show N" — local to the visit.
 */
const SandboxGroupCard = memo(function SandboxGroupCard({ group, initiallyOpen = false, runningIds, onAction, onCompare, onOpenSource }) {
  const { t, i18n } = useTranslation('unified-profiles');
  const [open, setOpen] = useState(initiallyOpen);
  const toggle = useCallback(() => setOpen((value) => !value), []);
  const handleOpenSource = useCallback(() => onOpenSource(group), [onOpenSource, group]);

  const when = group.periodLabel ?? formatDateWithYear(group.occurredAt, i18n?.language);
  const ChannelIcon = CHANNEL_ICONS[group.channel] ?? FileText;

  return (
    <div data-testid="sandbox-group" data-group-id={group.id}>
      <CartContainer size="M" variant="neutral">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <span
                data-testid="sandbox-group-icon"
                className="flex size-8 shrink-0 items-center justify-center rounded-md border border-edge text-content-secondary"
              >
                <ChannelIcon size={16} aria-hidden />
              </span>
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="font-semibold text-content">{group.label}</span>
                <span className="text-xs text-content-muted">
                  {[when, t('knowledge.sandbox.group.claims', { count: group.claimCount })].filter(Boolean).join(' · ')}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {group.state === 'conflict' ? (
                <Badge variant="error" size="small">
                  {t('knowledge.sandbox.status.conflict')}
                </Badge>
              ) : null}
              {group.sourceType ? (
                <Badge variant="neutral" size="small">
                  {t(`knowledge.sourceTypes.${group.sourceType}`)}
                </Badge>
              ) : null}
              {group.sourceRef && onOpenSource ? (
                <Button variant="ghost" size="sm" rightIcon={ArrowUpRight} onClick={handleOpenSource}>
                  {t('knowledge.sandbox.group.open')}
                </Button>
              ) : null}
              <Button variant="ghost" size="sm" rightIcon={open ? ChevronUp : ChevronDown} aria-expanded={open} onClick={toggle}>
                {open ? t('knowledge.sandbox.group.hide') : t('knowledge.sandbox.group.show', { count: group.claims.length })}
              </Button>
            </div>
          </div>
          {open ? (
            <div className="flex flex-col divide-y divide-edge">
              {group.claims.map((claim) => (
                <SandboxClaimRow
                  key={claim.id}
                  claim={claim}
                  busy={runningIds.has(claim.id)}
                  onAction={onAction}
                  onCompare={onCompare}
                />
              ))}
            </div>
          ) : null}
        </div>
      </CartContainer>
    </div>
  );
});

export default SandboxGroupCard;
